import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money, onlyDigits } from "@/lib/api";
import Money from "@/components/Money";
import DuplicatePhoneModal from "@/components/DuplicatePhoneModal";
import BulkAssignSheet from "@/components/BulkAssignSheet";
import { findPhoneMatch } from "@/lib/clientHelpers";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, ChevronRight, MapPin, Phone, ScanLine, AlertTriangle, CheckSquare, Square, Users, X } from "lucide-react";
import { toast } from "sonner";

const DIAS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];

export default function Clientes() {
  const nav = useNavigate();
  const { session, isAdmin, isVendedor, isCobrador } = useAuth();
  const [rows, setRows] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [cobradores, setCobradores] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ nome: "", telefone: "", bairro: "", endereco: "", observacoes: "", rota_id: "" });
  const [dupMatch, setDupMatch] = useState(null);

  // seleção em massa (admin)
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showSheet, setShowSheet] = useState(false);
  const [applying, setApplying] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/clientes", { params: { q: q || undefined } });
    setRows(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/rotas").then((r) => setRotas(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (isAdmin) {
      api.get("/staff").then((r) => setCobradores(r.data.filter((s) => s.roles?.cobrador && s.active))).catch(() => {});
    }
  }, [isAdmin]);

  const rotaById = (id) => rotas.find((r) => r.id === id);

  const persistCreate = async () => {
    try {
      await api.post("/clientes", { ...form, rota_id: form.rota_id || null });
      toast.success("Cliente cadastrado");
      setShowNew(false);
      setForm({ nome: "", telefone: "", bairro: "", endereco: "", observacoes: "", rota_id: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Nome é obrigatório");
    if (form.telefone) {
      const match = await findPhoneMatch(form.telefone);
      if (match) {
        setDupMatch(match);
        return;
      }
    }
    persistCreate();
  };

  const canCreate = isAdmin || isVendedor;
  const vendedorOnly = isVendedor && !isAdmin && !isCobrador;

  // ======= Seleção =======
  const enterSelectMode = () => { setSelectMode(true); };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const toggleId = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelected(new Set(rows.map((c) => c.id)));
  };

  const onCardTouchStart = (id) => {
    if (!isAdmin) return;
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      if (!selectMode) setSelectMode(true);
      setSelected((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }, 450);
  };
  const onCardTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const onCardClick = (e, id) => {
    if (longPressTriggeredRef.current) {
      e.preventDefault();
      longPressTriggeredRef.current = false;
      return;
    }
    if (selectMode) {
      e.preventDefault();
      toggleId(id);
    }
  };

  const applyBulk = async (payload, resetSheet) => {
    setApplying(true);
    try {
      const r = await api.post("/clientes/bulk-assign", {
        cliente_ids: Array.from(selected),
        ...payload,
      });
      const { updated = 0, failed = 0 } = r.data || {};
      if (failed > 0) {
        toast.warning(`${updated} cliente${updated !== 1 ? "s" : ""} atualizado${updated !== 1 ? "s" : ""}, ${failed} falhou`);
      } else {
        toast.success(`${updated} cliente${updated !== 1 ? "s" : ""} atualizado${updated !== 1 ? "s" : ""}`);
      }
      resetSheet && resetSheet();
      setShowSheet(false);
      exitSelectMode();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aplicar");
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <div className={selectedCount > 0 ? "pb-24" : ""}>
      <header className="mb-4">
        {vendedorOnly && (
          <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--terracotta)]">
            Olá, {session?.staff?.name?.split(" ")[0]} — Vendedor
          </div>
        )}
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Clientes</h1>
        <p className="text-sm text-stone-500 mt-1">Sua caderneta digital de clientes.</p>
      </header>

      {vendedorOnly && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Link to="/scan" data-testid="btn-quick-scan-ai" className="btn-primary text-sm">
            <ScanLine className="w-4 h-4" /> Escanear Carnê
          </Link>
          <Link to="/nova-venda" data-testid="btn-nova-venda" className="btn-primary text-sm" style={{ background: "var(--coal)" }}>
            <Plus className="w-4 h-4" /> Nova Venda
          </Link>
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="input-client-search"
            placeholder="Buscar por nome..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-12 pl-10 pr-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
          />
        </div>
        {isAdmin && !selectMode && (
          <button
            data-testid="btn-enter-select-mode"
            onClick={enterSelectMode}
            className="h-12 px-3 rounded-xl border border-stone-300 bg-white font-bold text-stone-700 text-sm inline-flex items-center gap-1"
            title="Selecionar vários"
          >
            <CheckSquare className="w-4 h-4" /> Selecionar
          </button>
        )}
        {!selectMode && (
          <button
            data-testid="btn-add-new-client"
            onClick={() => setShowNew(!showNew)}
            className={`btn-primary px-4 ${!canCreate ? "hidden" : ""}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {selectMode && (
        <div className="mb-3 flex items-center justify-between bg-[color:var(--terracotta-light)] border border-[color:var(--terracotta)]/30 rounded-xl px-3 py-2" data-testid="select-mode-bar">
          <div className="text-sm">
            <b data-testid="select-count">{selectedCount}</b> selecionado{selectedCount !== 1 ? "s" : ""}
            {selectedCount > 0 && (
              <>
                {" · "}
                <button data-testid="btn-clear-selection" onClick={() => setSelected(new Set())} className="text-[color:var(--terracotta)] font-bold">
                  Limpar seleção
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {q.trim() && rows.length > 0 && (
              <button
                data-testid="btn-select-all-filtered"
                onClick={selectAllFiltered}
                className="text-xs font-bold text-[color:var(--terracotta)]"
              >
                Selecionar todos filtrados ({rows.length})
              </button>
            )}
            <button data-testid="btn-exit-select-mode" onClick={exitSelectMode} className="w-8 h-8 rounded-full bg-white border border-stone-300 flex items-center justify-center" title="Sair do modo seleção">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showNew && !selectMode && (
        <form onSubmit={create} className="carne-card p-4 mb-4 animate-slide-up space-y-3" data-testid="new-client-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required data-testid="new-client-nome" placeholder="Nome*" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
            <input data-testid="new-client-telefone" placeholder="WhatsApp (5511...)" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
            <input data-testid="new-client-bairro" placeholder="Bairro" value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
            <input data-testid="new-client-endereco" placeholder="Endereço" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Rota de cobrança</label>
            <select
              data-testid="new-client-rota"
              value={form.rota_id}
              onChange={(e) => setForm({ ...form, rota_id: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            >
              <option value="">— Sem rota —</option>
              {rotas.map((r) => (
                <option key={r.id} value={r.id}>{r.nome} ({DIAS_SHORT[r.dia_semana]})</option>
              ))}
            </select>
            {rotas.length === 0 && (
              <div className="text-[11px] text-stone-500 mt-1">
                Nenhuma rota cadastrada. {isAdmin && <a href="/rotas" className="text-[color:var(--terracotta)] font-bold">Criar rota</a>}
              </div>
            )}
          </div>
          <textarea data-testid="new-client-obs" placeholder="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="w-full min-h-[70px] p-3 rounded-xl border border-stone-200 bg-white" />
          <button data-testid="btn-save-client" type="submit" className="btn-primary w-full">Salvar cliente</button>
        </form>
      )}

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      <div className="space-y-3" data-testid="client-list-container">
        {rows.map((c) => {
          const isSel = selected.has(c.id);
          const card = (
            <div
              data-testid={`client-card-${c.id}`}
              className={`carne-card p-4 flex items-start gap-3 hover:shadow-md transition-shadow block ${isSel ? "ring-2 ring-[color:var(--terracotta)]" : ""}`}
              onClick={(e) => onCardClick(e, c.id)}
              onTouchStart={() => onCardTouchStart(c.id)}
              onTouchEnd={onCardTouchEnd}
              onTouchMove={onCardTouchEnd}
              onMouseDown={() => onCardTouchStart(c.id)}
              onMouseUp={onCardTouchEnd}
              onMouseLeave={onCardTouchEnd}
            >
              {selectMode && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleId(c.id); }}
                  data-testid={`select-check-${c.id}`}
                  className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSel ? "bg-[color:var(--terracotta)] border-[color:var(--terracotta)]" : "border-stone-300 bg-white"}`}
                  aria-label={isSel ? "Desmarcar" : "Marcar"}
                >
                  {isSel && <CheckSquare className="w-4 h-4 text-white" strokeWidth={3} />}
                </button>
              )}
              <div className="w-11 h-11 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] flex items-center justify-center font-display text-lg font-bold shrink-0">
                {c.nome[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display text-base font-bold text-stone-900 truncate">{c.nome}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    {c.remarcacoes_count > 2 && (
                      <span data-testid={`badge-reincidente-${c.id}`} className="text-[10px] font-bold uppercase tracking-wider text-amber-900 bg-amber-200 border border-amber-300 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" title={`${c.remarcacoes_count} remarcações`}>
                        <AlertTriangle className="w-3 h-3" /> Reincidente
                      </span>
                    )}
                    {c.parcelas_atrasadas > 0 && (
                      <span className="stamp-overdue">{c.parcelas_atrasadas} atras.</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  {c.telefone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefone}</span>}
                  {c.bairro && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.bairro}</span>}
                  {c.rota_id && rotaById(c.rota_id) && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] px-1.5 py-0.5 rounded">
                      {rotaById(c.rota_id).nome} • {DIAS_SHORT[rotaById(c.rota_id).dia_semana]}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Saldo devedor</div>
                    <div className="font-mono-num text-base font-bold text-stone-900"><Money v={c.saldo_devedor} /></div>
                  </div>
                  {!selectMode && <ChevronRight className="w-5 h-5 text-stone-400" />}
                </div>
              </div>
            </div>
          );
          return selectMode ? (
            <div key={c.id} className="cursor-pointer">{card}</div>
          ) : (
            <Link key={c.id} to={`/clientes/${c.id}`} className="block">{card}</Link>
          );
        })}
        {!loading && rows.length === 0 && (
          <div className="text-center py-10 text-stone-500 text-sm">
            Nenhum cliente ainda. <button onClick={() => setShowNew(true)} className="text-[color:var(--terracotta)] font-bold">Cadastrar o primeiro</button>
          </div>
        )}
      </div>

      {selectMode && selectedCount > 0 && (
        <div className="fixed left-0 right-0 bottom-16 px-4 pb-2 z-40 pointer-events-none" data-testid="bulk-action-bar">
          <div className="max-w-md mx-auto bg-[color:var(--terracotta)] text-white rounded-2xl shadow-xl px-4 py-3 flex items-center justify-between pointer-events-auto">
            <div className="font-bold">
              <span data-testid="bulk-count-bar">{selectedCount}</span> selecionado{selectedCount !== 1 ? "s" : ""}
            </div>
            <button
              data-testid="btn-open-bulk-sheet"
              onClick={() => setShowSheet(true)}
              className="bg-white text-[color:var(--terracotta)] font-bold h-10 px-4 rounded-xl inline-flex items-center gap-1"
            >
              <Users className="w-4 h-4" /> Atribuir…
            </button>
          </div>
        </div>
      )}

      <BulkAssignSheet
        open={showSheet}
        count={selectedCount}
        rotas={rotas}
        cobradores={cobradores}
        applying={applying}
        onCancel={() => setShowSheet(false)}
        onApply={applyBulk}
      />

      <DuplicatePhoneModal
        open={!!dupMatch}
        existing={dupMatch}
        onUseExisting={(ex) => { setDupMatch(null); setShowNew(false); window.location.href = `/clientes/${ex.id}`; }}
        onCreateAnyway={() => { setDupMatch(null); persistCreate(); }}
        onCancel={() => setDupMatch(null)}
        mode="manual"
      />
    </div>
  );
}
